import { GeckoColorScale } from 'color-scale-kit'
import {
  CategoricalSummaryStatistics,
  QuantitativeSummaryStatistics,
  useFetchSummaryStatsSwr,
} from 'features/dataLayers'
import { getDeviationToleranceThreshold } from 'features/dataLayers/tolerances/deviationTolerances'
import { useMemo } from 'react'
import {
  DataType,
  GeckoColorScaleThreshold,
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeckoView,
  LIDAR_DEVIATION_VIEW_IDS,
} from 'types'

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

//------------------------------------------------------------------------------

/**
 * A simple hook which figures out what color scale to use when displaying a
 * given CDF. Note that this is generally derived from their GeckoView.
 * @param cdf            The component data frame
 * @param colorThreshold The min max thresholds for the color scale
 */
export const useColorScale = (
  cdf: GeckoComponentDataFrame | undefined,
  colorThreshold: GeckoColorScaleThreshold | undefined
): {
  colorScale: GeckoColorScale
  colorScaleMin: number | undefined
  colorScaleMax: number | undefined
  isLoading: boolean
} => {
  //----------------------------------------------------------------------------

  const measurementUnit = cdf?.data.params.measurement_unit
  const colorcet = cdf?.data.params.view.colorcet
  const { data, isLoading } = useFetchSummaryStatsSwr(cdf)
  const valueRange = data?.value_range

  const colorscaleData = useColorScaleNonCdf({
    measurementUnit,
    colorcet,
    valueRange,
    colorThreshold,
    isLoading,
  })

  return useMemo(
    () => ({
      ...colorscaleData,
      isLoading,
    }),
    [colorscaleData, isLoading]
  )
}

export function useColorScaleNonCdf({
  measurementUnit,
  colorcet,
  valueRange,
  colorThreshold,
  isLoading = false,
}: {
  measurementUnit?: GeckoMeasurementUnit
  colorcet?: GeckoView['colorcet']
  valueRange?: GeckoMeasurementRange
  colorThreshold?: GeckoColorScaleThreshold
  isLoading?: boolean
}) {
  return useMemo(() => {
    // Early return for loading or missing data (after EMAT categorical check)
    if (!measurementUnit || !valueRange || isLoading || !colorcet) {
      return {
        colorScale: colorcet?.customColorScale
          ? new GeckoColorScale({
              customColorScale: colorcet.customColorScale,
            })
          : new GeckoColorScale({}),
        colorScaleMin: undefined,
        colorScaleMax: undefined,
      }
    }

    const isValidCategorical = measurementUnit.dataType === DataType.Categorical

    // we don't want to clip out-of-range values for categorical units, because
    // we want to be able to list all potential options with their color.
    const rangeMin = isValidCategorical
      ? GeckoMeasurement.CreateFromValue(
          measurementUnit.acceptableValueRange[0],
          measurementUnit
        )
      : valueRange.min
    const rangeMax = isValidCategorical
      ? GeckoMeasurement.CreateFromValue(
          measurementUnit.acceptableValueRange[1],
          measurementUnit
        )
      : valueRange.max
    const rangeMid = rangeMin.plus(rangeMax).scale(0.5)

    // Prefer color scale values set in component-defaults, but default to
    // the full value range if there is none
    const convertedCT = colorThreshold?.convertTo(measurementUnit)
    const scaleMin = (convertedCT?.min ?? rangeMin).num
    const scaleMid = (convertedCT?.mid ?? rangeMid).num
    const scaleMax = (convertedCT?.max ?? rangeMax).num

    const { colorcetId, start, middle, end } = colorcet
    const valuesToColorRange =
      // Populate all important values, from range min/mid/max to custom values
      // from component-defaults, and use a set to ensure uniqueness (we don't
      // want to sample colors for the same value twice)
      Array.from(
        new Set(
          [rangeMin.num, rangeMax.num, scaleMin, scaleMid, scaleMax].filter(
            (value) => value >= rangeMin.num && value <= rangeMax.num
          )
        )
      )
        // Sort the values from min to max
        .sort((a, b) => a - b)
        // Turn these values into pairs of values-to-Colorcet color values
        // (i.e. 0-1 values along a Colorcet color scale)
        .map<[number, number]>((value) => {
          if (value <= scaleMin) return [value, start]
          if (value === scaleMid) return [value, middle]
          if (value >= scaleMax) return [value, end]
          return [
            value,
            value < scaleMid
              ? lerp(start, middle, (value - scaleMin) / (scaleMid - scaleMin))
              : lerp(middle, end, (value - scaleMid) / (scaleMax - scaleMid)),
          ]
        })
    if (valuesToColorRange.length === 0) {
      return {
        colorScale: colorcet.customColorScale
          ? new GeckoColorScale({
              customColorScale: colorcet.customColorScale,
            })
          : new GeckoColorScale({}),
        colorScaleMin: undefined,
        colorScaleMax: undefined,
      }
    }

    return {
      colorScale: colorcet.customColorScale
        ? new GeckoColorScale({
            customColorScale: colorcet.customColorScale,
            valuesToColorRange,
          })
        : new GeckoColorScale({
            colorcetId: colorcetId,
            valuesToColorRange,
          }),
      colorScaleMin: valuesToColorRange.at(0)?.at(0),
      colorScaleMax: valuesToColorRange.at(-1)?.at(0),
    }
  }, [isLoading, measurementUnit, colorThreshold, colorcet, valueRange])
}

/**
 * A function which determines the default color scale for a given CDF.
 *
 * TODO: Create a generic way to configure per-view color scale defaults
 */
export function determineDefaultColorScaleThreshold(
  selectedCdfView: GeckoView | undefined,
  selectedCdfMeasurementUnit: GeckoMeasurementUnit | undefined,
  selectedCdfStats:
    | QuantitativeSummaryStatistics
    | CategoricalSummaryStatistics
    | undefined,
  selectedComponentModel?: GeckoComponentModel
): GeckoColorScaleThreshold | undefined {
  return determineDefaultColorScaleThresholdNonCdf(
    selectedCdfView,
    selectedCdfMeasurementUnit,
    selectedCdfStats?.value_range,
    selectedComponentModel
  )
}

export function determineDefaultColorScaleThresholdNonCdf(
  selectedCdfView: GeckoView | undefined,
  measurementUnit?: GeckoMeasurementUnit,
  valueRange?: GeckoMeasurementRange,
  selectedComponentModel?: GeckoComponentModel
): GeckoColorScaleThreshold | undefined {
  // Views with an API 653 tolerance get their threshold from the asset
  // dimensions, so this check comes before the fixed per-view defaults below
  const toleranceThreshold = getDeviationToleranceThreshold(
    selectedCdfView,
    selectedComponentModel
  )
  if (toleranceThreshold) return toleranceThreshold

  if (selectedCdfView === GeckoView.RemainingLife) {
    return GeckoColorScaleThreshold.CreateFromValues(
      0,
      50,
      150,
      GeckoMeasurementUnit.Years
    )
  } else if (
    selectedCdfView &&
    LIDAR_DEVIATION_VIEW_IDS.has(selectedCdfView.id)
  ) {
    return GeckoColorScaleThreshold.CreateFromValues(
      -3.5,
      0,
      3.5,
      GeckoMeasurementUnit.Inches
    )
  } else if (measurementUnit?.dataType === DataType.Categorical) {
    return GeckoColorScaleThreshold.CreateFromValues(
      measurementUnit.acceptableValueRange[0],
      (measurementUnit.acceptableValueRange[0] +
        measurementUnit.acceptableValueRange[1]) /
        2,
      measurementUnit.acceptableValueRange[1],
      measurementUnit
    )
  } else if (valueRange) {
    return GeckoColorScaleThreshold.CreateFromMeasurements(
      valueRange.min,
      valueRange.min.plus(valueRange.max).scale(0.5),
      valueRange.max
    )
  }

  return undefined
}
